import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { BUILDINGS } from '../../lib/workerData';

const REPORT_TYPES = [
  'Missed duties',
  'Duties not completed at required frequency',
  'Cleaning standard not met',
  'Wrong or non-approved product used',
  'LEED non-compliance (Buildings E or F)',
  'Recycling disposed in garbage',
  'Dispensers not refilled',
  'Garbage not removed from building',
  'Cleaning equipment not sanitary',
  'Checklist not completed',
  'Checklist not submitted',
  'Worker not in uniform',
  'Failure to resolve previous report',
  'Other',
];

// Parse 'YYYY-MM-DD' as a local date — new Date(dateStr) reads it as UTC
// midnight, which for Newfoundland's negative offset falls on the previous
// local evening, occasionally grouping a report into the wrong month in
// the filter tabs.
function getMonthKey(dateStr) {
  if (!dateStr) return '';
  const [y, m] = dateStr.split('-');
  if (!y || !m) return '';
  return `${y}-${m}`;
}

function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatMonth(key) {
  if (!key) return '';
  const [year, month] = key.split('-');
  return new Date(year, month - 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
}

export default function NegativeReports() {
  const { profile: myProfile } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [filterMonth, setFilterMonth] = useState('');
  const [actionError, setActionError] = useState(null);

  function defaultForm() {
    return {
      report_date: localDateStr(),
      building_id: '',
      building_name: '',
      report_type: '',
      description: '',
      // Free-text name of who at the Town reported this — NOT the same as
      // `reported_by`, which is a uuid FK to the staff profile that logged
      // the report (set automatically on save).
      received_from: 'Jason Lawrence',
      status: 'open',
      resolution: '',
    };
  }

  const loadReports = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('negative_reports')
      .select('*')
      .order('report_date', { ascending: false });
    if (data) setReports(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  async function handleSave() {
    if (!form.report_date || !form.report_type) return;
    setSaving(true);
    setSaveError(null);
    const { error } = await supabase.from('negative_reports').insert([{
      ...form,
      reported_by: myProfile?.id || null,
    }]);
    if (error) {
      setSaving(false);
      setSaveError(error.message);
      return;
    }
    await loadReports();
    setShowForm(false);
    setSaving(false);
  }

  async function handleResolve(report) {
    const resolution = window.prompt('Describe how this was resolved:');
    if (!resolution) return;
    setActionError(null);
    // Keep the legacy `resolved` boolean column in sync with `status` —
    // the app only ever read/wrote `status`, leaving `resolved` stuck at
    // its default `false` for every report regardless of actual state.
    const { error } = await supabase.from('negative_reports').update({ status: 'resolved', resolution, resolved: true }).eq('id', report.id);
    if (error) {
      setActionError(`Could not mark resolved: ${error.message}`);
      return;
    }
    await loadReports();
  }

  async function handleReopen(report) {
    if (!window.confirm('Reopen this report? It will count toward this month\'s penalty tracker again.')) return;
    setActionError(null);
    const { error } = await supabase.from('negative_reports').update({ status: 'open', resolved: false }).eq('id', report.id);
    if (error) {
      setActionError(`Could not reopen report: ${error.message}`);
      return;
    }
    await loadReports();
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this report?')) return;
    setActionError(null);
    const { error } = await supabase.from('negative_reports').delete().eq('id', id);
    if (error) {
      setActionError(`Could not delete report: ${error.message}`);
      return;
    }
    await loadReports();
  }

  // Group by month
  const months = [...new Set(reports.map(r => getMonthKey(r.report_date)))];
  const currentMonthKey = getMonthKey(localDateStr());
  const thisMonthReports = reports.filter(r => getMonthKey(r.report_date) === currentMonthKey && r.status !== 'resolved');
  const deductionTriggered = thisMonthReports.length >= 5;
  const filtered = filterMonth ? reports.filter(r => getMonthKey(r.report_date) === filterMonth) : reports;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Negative Reports</div>
          <div style={s.subtitle}>Contract penalty tracker — TOP-25-27</div>
        </div>
        <button style={s.addBtn} onClick={() => { setForm(defaultForm()); setSaveError(null); setShowForm(true); }}>+ Log Report</button>
      </div>

      {actionError && (
        <div style={{ margin: '0 12px 12px', background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 8, padding: '10px 14px', color: '#B33A2E', fontSize: 13 }}>
          {actionError}
        </div>
      )}

      {/* Penalty status. Stays neutral until reports have loaded — an
          unloaded list would otherwise render "✓ All clear this month"
          with five empty dots, which is the opposite of the truth if
          reports do exist. */}
      <div style={{
        ...s.penaltyBar,
        background: loading ? '#F5F3EF' : (deductionTriggered ? '#FBF1EF' : 'rgba(47,122,79,0.08)'),
        borderColor: loading ? '#E4E0D8' : (deductionTriggered ? '#B33A2E' : '#2F7A4F'),
      }}>
        <div>
          <div style={{ color: loading ? '#5B6B72' : (deductionTriggered ? '#B33A2E' : '#2F7A4F'), fontWeight: 800, fontSize: 15 }}>
            {loading
              ? 'Checking this month\'s reports…'
              : deductionTriggered ? '⚠️ PENALTY TRIGGERED' : thisMonthReports.length === 0 ? '✓ All clear this month' : `${thisMonthReports.length} report${thisMonthReports.length !== 1 ? 's' : ''} this month`}
          </div>
          {!loading && (
            <div style={{ color: '#5B6B72', fontSize: 12, marginTop: 2 }}>
              {deductionTriggered
                ? '5+ reports this month — 10% invoice deduction applies'
                : `${5 - thisMonthReports.length} more report${5 - thisMonthReports.length !== 1 ? 's' : ''} before 10% deduction triggers`}
            </div>
          )}
        </div>
        {!loading && (
          <div style={{ display: 'flex', gap: 4 }} title={`${thisMonthReports.length} of 5 reports this month before a 10% deduction applies`}>
            {[1,2,3,4,5].map(n => (
              <div key={n} style={{
                width: 20, height: 20, borderRadius: '50%',
                background: n <= thisMonthReports.length ? (deductionTriggered ? '#B33A2E' : '#B5730A') : '#E4E0D8',
                border: '1px solid #E4E0D8',
              }} />
            ))}
          </div>
        )}
      </div>

      {/* Threshold reminder */}
      <div style={s.thresholdRow}>
        <div style={s.threshold}>
          <span style={{ color: '#2F7A4F', fontWeight: 700 }}>0–2</span>
          <span style={s.thresholdLabel}>All clear</span>
        </div>
        <div style={s.threshold}>
          <span style={{ color: '#B5730A', fontWeight: 700 }}>3–4</span>
          <span style={s.thresholdLabel}>Warning zone</span>
        </div>
        <div style={s.threshold}>
          <span style={{ color: '#B33A2E', fontWeight: 700 }}>5+</span>
          <span style={s.thresholdLabel}>10% deduction</span>
        </div>
        <div style={s.threshold}>
          <span style={{ color: '#5B6B72', fontWeight: 700 }}>3 months</span>
          <span style={s.thresholdLabel}>Contract at risk</span>
        </div>
      </div>

      {/* Month filter */}
      {months.length > 1 && (
        <div style={s.filterRow}>
          <button style={!filterMonth ? { ...s.filterBtn, background: '#f97316', color: '#FFFFFF' } : s.filterBtn}
            onClick={() => setFilterMonth('')}>All</button>
          {months.map(m => (
            <button key={m}
              style={filterMonth === m ? { ...s.filterBtn, background: '#f97316', color: '#FFFFFF' } : s.filterBtn}
              onClick={() => setFilterMonth(m)}>{formatMonth(m)}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={s.loading}>Loading reports...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          {reports.length === 0
            ? 'No negative reports logged. Keep it that way!'
            : 'No reports for selected month.'}
        </div>
      ) : (
        <div style={s.list}>
          {filtered.map(report => (
            <div key={report.id} style={{ ...s.card, borderLeftColor: report.status === 'resolved' ? '#2F7A4F' : '#B33A2E' }}>
              <div style={s.cardTop}>
                <div style={s.cardLeft}>
                  <div style={s.cardMeta}>{report.report_date} · {report.building_name || report.building_id || 'General'} · Reported by: {report.received_from || 'Town'}</div>
                  <div style={s.cardType}>{report.report_type}</div>
                  {report.description && <div style={s.cardDesc}>{report.description}</div>}
                  {report.resolution && <div style={s.resolution}>✓ Resolved: {report.resolution}</div>}
                </div>
                <div style={{ ...s.statusPill, background: report.status === 'resolved' ? 'rgba(47,122,79,0.10)' : '#FBF1EF', color: report.status === 'resolved' ? '#2F7A4F' : '#B33A2E' }}>
                  {report.status === 'resolved' ? 'Resolved' : 'Open'}
                </div>
              </div>
              {report.status === 'open' ? (
                <div style={s.cardActions}>
                  <button style={s.resolveBtn} onClick={() => handleResolve(report)}>✓ Mark Resolved</button>
                  <button style={s.deleteBtn} onClick={() => handleDelete(report.id)}>Delete</button>
                </div>
              ) : (
                <div style={s.cardActions}>
                  <button style={s.deleteBtn} onClick={() => handleReopen(report)}>↺ Reopen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>Log Negative Report</div>
              <button style={s.closeBtn} onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <label style={s.label}>DATE *</label>
              <input style={s.input} type="date" value={form.report_date}
                onChange={e => setForm(p => ({ ...p, report_date: e.target.value }))} />
              <label style={s.label}>BUILDING</label>
              <select style={s.input} value={form.building_id}
                onChange={e => {
                  const id = e.target.value;
                  const b = BUILDINGS.find(x => x.id === id);
                  setForm(p => ({ ...p, building_id: id, building_name: b ? (b.shortName || b.name) : '' }));
                }}>
                <option value="">— General / not building-specific —</option>
                {BUILDINGS.filter(b => b.status !== 'special').map(b => (
                  <option key={b.id} value={b.id}>{b.id} — {b.shortName || b.name}</option>
                ))}
              </select>
              <label style={s.label}>REPORT TYPE *</label>
              <select style={s.input} value={form.report_type}
                onChange={e => setForm(p => ({ ...p, report_type: e.target.value }))}>
                <option value="">Select type...</option>
                {REPORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <label style={s.label}>DESCRIPTION</label>
              <textarea style={s.textarea} rows={3}
                placeholder="Details about what was reported..."
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              <label style={s.label}>REPORTED BY (TOWN CONTACT)</label>
              <input style={s.input} value={form.received_from}
                onChange={e => setForm(p => ({ ...p, received_from: e.target.value }))} />
            </div>
            {saveError && (
              <div style={{ margin: '0 20px 12px', background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 8, padding: '10px 12px', color: '#B33A2E', fontSize: 12.5 }}>
                Could not save: {saveError}
              </div>
            )}
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              <button
                style={(!form.report_date || !form.report_type || saving) ? { ...s.saveBtn, opacity: 0.5 } : s.saveBtn}
                disabled={!form.report_date || !form.report_type || saving}
                onClick={handleSave}>{saving ? 'Saving...' : 'Log Report'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '0 0 60px', background: '#F5F3EF', minHeight: 'calc(100vh - 130px)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 12px' },
  title: { fontSize: 22, fontWeight: 800, color: '#1A2B33', letterSpacing: '-0.5px' },
  subtitle: { fontSize: 13, color: '#5B6B72', marginTop: 2 },
  addBtn: { background: '#B33A2E', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  penaltyBar: { margin: '0 12px 12px', border: '1px solid', borderRadius: 12, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  thresholdRow: { display: 'flex', gap: 8, padding: '0 12px 12px', flexWrap: 'wrap' },
  threshold: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '8px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 70 },
  thresholdLabel: { fontSize: 11, color: '#5B6B72', marginTop: 2 },
  filterRow: { display: 'flex', gap: 6, padding: '0 12px 12px', overflowX: 'auto' },
  filterBtn: { background: '#FFFFFF', color: '#5B6B72', border: '1px solid #E4E0D8', borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  loading: { padding: 40, textAlign: 'center', color: '#5B6B72' },
  empty: { margin: '0 12px', padding: 24, textAlign: 'center', color: '#5B6B72', fontSize: 14, border: '1px dashed #E4E0D8', borderRadius: 10 },
  list: { display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px' },
  card: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderLeft: '3px solid', borderRadius: 12, padding: 16 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  cardLeft: { flex: 1 },
  cardMeta: { fontSize: 11, color: '#5B6B72', marginBottom: 4 },
  cardType: { fontSize: 15, fontWeight: 700, color: '#1A2B33', marginBottom: 4 },
  cardDesc: { fontSize: 13, color: '#5B6B72', lineHeight: 1.5 },
  resolution: { fontSize: 12, color: '#2F7A4F', marginTop: 6 },
  statusPill: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, flexShrink: 0 },
  cardActions: { display: 'flex', gap: 8, marginTop: 10 },
  resolveBtn: { background: 'rgba(47,122,79,0.10)', border: '1px solid #2F7A4F', color: '#2F7A4F', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  deleteBtn: { background: 'transparent', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modal: { background: '#FFFFFF', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 12px', borderBottom: '1px solid #E4E0D8' },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#1A2B33' },
  closeBtn: { background: 'none', border: 'none', color: '#5B6B72', fontSize: 20, cursor: 'pointer' },
  modalBody: { padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  modalFooter: { padding: '14px 20px', borderTop: '1px solid #E4E0D8', display: 'flex', gap: 10 },
  label: { fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: '#5B6B72' },
  input: { width: '100%', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '11px 14px', color: '#1A2B33', fontSize: 15, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  textarea: { width: '100%', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '11px 14px', color: '#1A2B33', fontSize: 14, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  cancelBtn: { flex: 1, background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  saveBtn: { flex: 2, background: '#B33A2E', border: 'none', color: '#fff', borderRadius: 8, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer' },
};
